import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AccountPageShell } from "@/components/AccountPageShell";
import { TabletPinSetupPanel } from "@/components/tabletPin/TabletPinSetupPanel";
import { PersonalMouserCommercePanel } from "@/pages/personal/PersonalMouserCommercePanel";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Label } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintWithGlyph } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useAuth } from "@/components/AuthProvider";
import { ApiError, apiFetch } from "@/lib/api";
import {
  GOOGLE_WORKSPACE_SURFACES_KEY,
  parseGoogleWorkspaceSurfaces,
  type GoogleWorkspaceSurfaces,
} from "@/lib/googleWorkspaceSurfaces";
import { useIntegratorMe } from "@/lib/me-query";
import { isHubGoogleWorkspaceEmail } from "@/lib/hubSignInEmail";
import {
  clampNotificationArrivalDismissSeconds,
  MAX_NOTIFICATION_ARRIVAL_DISMISS_SECONDS,
  MIN_NOTIFICATION_ARRIVAL_DISMISS_SECONDS,
  readNotificationArrivalDismissMs,
  storeNotificationArrivalDismissMs,
} from "@/lib/notificationArrival";

function GoogleWorkspaceSurfacesCard(): JSX.Element {
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const queryKey = ["hub-ui-state", GOOGLE_WORKSPACE_SURFACES_KEY] as const;
  const surfaces = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiFetch<{ value: unknown }>(
        `/api/me/ui-state/${encodeURIComponent(GOOGLE_WORKSPACE_SURFACES_KEY)}`,
      );
      return parseGoogleWorkspaceSurfaces(response.value);
    },
  });
  const save = useMutation({
    mutationFn: (next: GoogleWorkspaceSurfaces) =>
      apiFetch(`/api/me/ui-state/${encodeURIComponent(GOOGLE_WORKSPACE_SURFACES_KEY)}`, {
        method: "PUT",
        body: { value: next },
      }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<GoogleWorkspaceSurfaces>(queryKey);
      queryClient.setQueryData(queryKey, next);
      return { previous };
    },
    onError: (error, _next, context) => {
      if (context?.previous != null) queryClient.setQueryData(queryKey, context.previous);
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not save Google Chat settings.",
      });
    },
  });
  const current = surfaces.data ?? { chat: false, calendar: false };
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Turns the floating Chat and Calendar buttons on for this account. Both stay off until you enable them.">
          Google Chat and Calendar
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-2 text-sm text-slate-800">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={current.chat}
            disabled={surfaces.isPending}
            onChange={(event) => save.mutate({ ...current, chat: event.target.checked })}
          />
          Chat
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={current.calendar}
            disabled={surfaces.isPending}
            onChange={(event) => save.mutate({ ...current, calendar: event.target.checked })}
          />
          Calendar
        </label>
      </CardBody>
    </Card>
  );
}

export function UserSettingsPage(): JSX.Element {
  const { session, loading } = useAuth();
  const { data: me } = useIntegratorMe();
  const signedInEmail = session?.user.email?.trim().toLowerCase() ?? "";
  const workspaceAccount = isHubGoogleWorkspaceEmail(signedInEmail);
  const displayName = me?.user.displayName?.trim() ?? null;
  const [savedDismissSeconds, setSavedDismissSeconds] = useState(() => readNotificationArrivalDismissMs() / 1000);
  const [dismissSeconds, setDismissSeconds] = useState(() => String(readNotificationArrivalDismissMs() / 1000));
  const parsedDismissSeconds = Number.parseInt(dismissSeconds, 10);
  const dismissSecondsValid = Number.isFinite(parsedDismissSeconds)
    && parsedDismissSeconds >= MIN_NOTIFICATION_ARRIVAL_DISMISS_SECONDS
    && parsedDismissSeconds <= MAX_NOTIFICATION_ARRIVAL_DISMISS_SECONDS;
  const dismissDirty = dismissSecondsValid && parsedDismissSeconds !== savedDismissSeconds;

  useEffect(() => {
    if (window.location.hash === "#tablet-pin") {
      const target = document.getElementById("tablet-pin");
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  return (
    <AccountPageShell title="Settings" loading={loading}>
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardBody className="space-y-1 text-sm text-slate-700">
          {displayName ? <p className="font-medium text-slate-900">{displayName}</p> : null}
          <p>{signedInEmail}</p>
        </CardBody>
      </Card>

      <GoogleWorkspaceSurfacesCard />

      <Card id="tablet-pin">
        <CardHeader>
          <CardTitle>Manufacturing tablet PIN</CardTitle>
        </CardHeader>
        <CardBody>
          <TabletPinSetupPanel signedInEmail={signedInEmail} workspaceAccount={workspaceAccount} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center gap-2">
          <CardTitle>Notification window</CardTitle>
          <HoverHintWithGlyph hoverHint="New notifications, and snoozed notifications when they come back, stack under your avatar while you are on the hub. Hide leaves a notification unread, the same as letting the timer finish. This timer is saved in this browser." />
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="notification-arrival-dismiss-seconds">Hide after (seconds)</Label>
            <input
              id="notification-arrival-dismiss-seconds"
              type="number"
              min={MIN_NOTIFICATION_ARRIVAL_DISMISS_SECONDS}
              max={MAX_NOTIFICATION_ARRIVAL_DISMISS_SECONDS}
              inputMode="numeric"
              className="h-9 w-32 rounded-md border border-slate-300 px-2 text-sm text-slate-900"
              value={dismissSeconds}
              onChange={(event) => setDismissSeconds(event.target.value)}
            />
          </div>
          <HubSaveButton
            dirty={dismissDirty}
            disabled={!dismissSecondsValid}
            blockedHint={`Enter a whole number from ${MIN_NOTIFICATION_ARRIVAL_DISMISS_SECONDS} to ${MAX_NOTIFICATION_ARRIVAL_DISMISS_SECONDS}.`}
            onClick={() => {
              const seconds = clampNotificationArrivalDismissSeconds(parsedDismissSeconds);
              storeNotificationArrivalDismissMs(seconds * 1000);
              setSavedDismissSeconds(seconds);
              setDismissSeconds(String(seconds));
            }}
          >
            Save
          </HubSaveButton>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mouser cart (personal API key)</CardTitle>
        </CardHeader>
        <CardBody>
          <PersonalMouserCommercePanel />
        </CardBody>
      </Card>
    </AccountPageShell>
  );
}
